import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config';

const Payment: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect admins away from payment page
  useEffect(() => {
    if (user?.is_admin) {
      navigate('/admin');
    }
  }, [user, navigate]);

  const handlePayment = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // In a real application, this would integrate with a payment provider like Stripe
      const response = await fetch(`${API_BASE_URL}/api/payment/process`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: user?.id,
          amount: 299.99, // Conference ticket price
        }),
      });

      if (!response.ok) {
        throw new Error('Payment failed. Please try again.');
      }

      const data = await response.json();
      if (data.success) {
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred during payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-white py-12">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          {/* Header */}
          <div className="bg-[#00529B] px-6 py-4">
            <h2 className="text-2xl font-bold text-white">Conference Registration</h2>
          </div>

          {/* Content */}
          <div className="p-6">
            <div className="space-y-6">
              {/* Price Card */}
              <div className="bg-[#00529B]/5 rounded-lg p-6 border border-[#00529B]/10">
                <h3 className="text-xl font-semibold text-[#00529B] mb-2">
                  Conference Ticket
                </h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl font-bold text-gray-900">$299.99</span>
                  <span className="ml-2 text-gray-500">USD</span>
                </div>
                <ul className="space-y-3 text-gray-600">
                  <li className="flex items-center">
                    <svg className="h-5 w-5 text-[#00529B] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Full conference access
                  </li>
                  <li className="flex items-center">
                    <svg className="h-5 w-5 text-[#00529B] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Networking opportunities
                  </li>
                  <li className="flex items-center">
                    <svg className="h-5 w-5 text-[#00529B] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Conference materials
                  </li>
                  <li className="flex items-center">
                    <svg className="h-5 w-5 text-[#00529B] mr-2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Lunch and refreshments
                  </li>
                </ul>
              </div>

              {/* Payment Form */}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                  {error}
                </div>
              )}

              <button
                onClick={handlePayment}
                disabled={loading}
                className="w-full bg-[#00529B] text-white py-3 px-4 rounded-lg hover:bg-[#00529B]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Processing...' : 'Proceed to Payment'}
              </button>

              <p className="text-sm text-gray-500 text-center">
                Secure payment processing. Your information is protected.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;
